import GameState, { getXYindex } from "./gameState";
import { EntityKind, matcher, MeleeEnemy } from "./entityKinds";
import { Dir } from "../entities/Player";
import { GameEvents } from "../constants";
import { Rules } from "./rules/overlap";

interface IMeleeMove {
  dirs: Dir[];
  entity: MeleeEnemy;
}

interface IExtendedEntityBroken {
  sharedBlockId: number;
  id: number;
  x: number;
  y: number;
}

// Handles the game turn logic happening.
export class GameTurnLogic {
  private state: GameState;
  private dirtyTiles: Set<number>;
  private meleeEnemies: MeleeEnemy[];
  public extendedBodiesBroken: Map<number, IExtendedEntityBroken>;

  constructor(state: GameState) {
    this.state = state;
    this.dirtyTiles = new Set();
    this.meleeEnemies = [];
    this.extendedBodiesBroken = new Map();
  }

  afterPlayerLogic() {
    this.state.mutateGameState((state) => {
      if (this.state.turn_gameState.player) {
        let { x, y, dir } = this.state.turn_gameState.player;

        state.board[getXYindex(x, y)].push({
          kind: EntityKind.PlayerBody,
          x,
          y,
          id: -1,
        });

        if (dir === Dir.Right) {
          x += 1;
        } else if (dir === Dir.Left) {
          x -= 1;
        } else if (dir === Dir.Up) {
          y -= 1;
        } else if (dir === Dir.Down) {
          y += 1;
        }
        state.board[getXYindex(x, y)].push({
          kind: EntityKind.PlayerGun,
          x: -1,
          y: -1,
          id: -1,
        });
      }
    });
    this.applyOverlapRules(true);
    do {
      this.applyOverlapRules();
      this.state.game_events?.emit(GameEvents.AddAndRemoveEntities);
      this.state._addEntities();
    } while (this.dirtyTiles.size > 0);

    //Process and group entities:
    this.state.turn_gameState.board.forEach((entities) => {
      entities.forEach((entity) => {
        matcher<void>({
          [EntityKind.Empty]: () => {},
          [EntityKind.Crate]: () => {},
          [EntityKind.StaticWall]: () => {},
          [EntityKind.PressurePlate]: () => {},
          [EntityKind.PlayerBullet]: () => {},
          [EntityKind.MeleeEnemy]: (e) => this.meleeEnemies.push(e),
          [EntityKind.PlayerGun]: () => {},
          [EntityKind.PlayerBody]: () => {},
          [EntityKind.Door]: () => {},
          [EntityKind.TDamage]: () => {},
          [EntityKind.WinLevel]: () => {},
          [EntityKind.ExtendedBlock]: () => {},
          [EntityKind.LazorToggle]: () => {},
        })(entity);
      });
    });

    this.tickWorld();
    this.applyOverlapRules(true);
    while (this.dirtyTiles.size > 0) {
      this.applyOverlapRules();
      this.state.game_events?.emit(GameEvents.AddAndRemoveEntities);
      this.state._addEntities();
    }

    this.state.setFinalStatesFromSteadyState();
    this.state.finalizeGameTurn();
  }

  // Overlap rules go like so.
  // 1. Run over all the tiles and figure out the interactions.
  applyOverlapRules(allDirty = false) {
    this.state.steady_state = true;
    if (allDirty) {
      this.dirtyTiles.clear();
      this.state.turn_gameState.board.forEach((entities) => {
        Rules.handleOverlap(entities, this.state, this.dirtyTiles, this);
      });
    } else {
      const dirtyTiles = [...this.dirtyTiles];
      this.dirtyTiles.clear();
      for (const dirtyTile of dirtyTiles) {
        Rules.handleOverlap(
          this.state.turn_gameState.board[dirtyTile],
          this.state,
          this.dirtyTiles,
          this
        );
      }
    }
  }

  // Run through groups of entities in a very specific order.
  // Traps go first.
  // Enemies will go second.
  tickWorld() {
    this.tickMeleeEnemies();
  }

  tickMeleeEnemies() {
    const submittedTurns: IMeleeMove[] = [];
    const player = this.state.turn_gameState.player;
    if (player === undefined) {
      console.log("player undefined");
      return;
    }
    for (const entity of this.meleeEnemies) {
      if (entity.exhaustedTurn) {
        continue;
      }
      const xDiff = Math.sign(player.x - entity.x);
      const yDiff = Math.sign(player.y - entity.y);
      if (
        entity.entity?.().checkIfValidMove(entity.x + xDiff, entity.y + yDiff)
      ) {
        const turnDirs = [];
        if (yDiff === 1) {
          turnDirs.push(Dir.Down);
        } else if (yDiff === -1) {
          turnDirs.push(Dir.Up);
        }
        if (xDiff === -1) {
          turnDirs.push(Dir.Left);
        } else if (xDiff === 1) {
          turnDirs.push(Dir.Right);
        }
        submittedTurns.push({
          dirs: turnDirs,
          entity,
        });
      }
      if (yDiff === 1) {
        submittedTurns.push({
          dirs: [Dir.Down],
          entity,
        });
      } else if (yDiff === -1) {
        submittedTurns.push({
          dirs: [Dir.Up],
          entity,
        });
      }
      if (xDiff === -1) {
        submittedTurns.push({
          dirs: [Dir.Left],
          entity,
        });
      } else if (xDiff === 1) {
        submittedTurns.push({
          dirs: [Dir.Right],
          entity,
        });
      }
    }

    // negative if first value is smaller.
    submittedTurns.sort((a: IMeleeMove, b: IMeleeMove) => {
      if (a.dirs.length < b.dirs.length) {
        return 1;
      } else if (a.dirs.length > b.dirs.length) {
        return -1;
      }
      return 0;
    });

    let entityMoved = false;
    do {
      entityMoved = false;
      for (const { dirs, entity } of submittedTurns) {
        entityMoved =
          entityMoved || entity.entity?.().playerMadeTurn(dirs) || false;
      }
    } while (entityMoved);
  }
}
